import { Search } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/field';
import { Callout } from '@/components/patterns/callout';
import { dataSource } from '@/data';
import { mensagemDoErro } from '@/data/http';
import { definirPersonificacao } from '@/data/personificacao';
import { InstalacaoFlags } from '@/components/patterns/status-badge';
import type { InstalacaoBusca } from '@/data/types';
import { cn } from '@/lib/utils';

const CARACTERES_MINIMOS = 2;
const DEBOUNCE_MS = 300;

/**
 * "Personificar Instalação" — sobretela restrita pela capability
 * `personificar-instalacao`. Busca entre TODAS as instalações do sistema
 * (não só as do usuário) e, ao confirmar, substitui o universo inteiro do
 * usuário por essa única instalação, com acesso de administrador completo.
 *
 * Não usa `SearchSelect` (o combo padrão do app): aquele filtra uma lista já
 * carregada em memória — aqui a lista é o sistema inteiro, a busca precisa
 * ser no servidor, com debounce.
 */
export function PersonificarInstalacaoDialog({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const [busca, setBusca] = React.useState('');
  const [resultados, setResultados] = React.useState<InstalacaoBusca[]>([]);
  const [carregando, setCarregando] = React.useState(false);
  const [erro, setErro] = React.useState<string | null>(null);
  const [confirmando, setConfirmando] = React.useState(false);
  const [selecionada, setSelecionada] = React.useState<InstalacaoBusca | null>(null);

  React.useEffect(() => {
    if (!aberto) return;
    setBusca('');
    setResultados([]);
    setErro(null);
    setSelecionada(null);
  }, [aberto]);

  React.useEffect(() => {
    if (!aberto) return;
    const termo = busca.trim();
    if (termo.length < CARACTERES_MINIMOS) {
      setResultados([]);
      setErro(null);
      setCarregando(false);
      return;
    }

    setCarregando(true);
    const controller = new AbortController();
    const idDebounce = window.setTimeout(() => {
      dataSource
        .buscarInstalacoesParaPersonificar(termo, controller.signal)
        .then((itens) => {
          setResultados(itens);
          setErro(null);
        })
        .catch((causa: unknown) => {
          if (causa instanceof DOMException && causa.name === 'AbortError') return;
          setResultados([]);
          setErro(
            causa instanceof Error
              ? causa.message
              : 'Não foi possível buscar instalações para personificar.',
          );
        })
        .finally(() => setCarregando(false));
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(idDebounce);
      controller.abort();
    };
  }, [busca, aberto]);

  async function confirmar() {
    if (!selecionada || confirmando) return;

    // A trava contra personificação simultânea é o único passo que pode
    // recusar de verdade — por isso vem ANTES de tudo, com erro real (sem
    // try/catch): se outra pessoa já estiver personificando esta instalação,
    // a sessão não abre. Auditoria continua melhor-esforço (não deveria
    // travar o suporte por um log que falhou); a trava é o oposto — é
    // JUSTAMENTE o controle, não pode ser ignorada em silêncio.
    if (selecionada.instalacaoInfraId != null) {
      setConfirmando(true);
      setErro(null);
      try {
        await dataSource.abrirOuRenovarSessaoPersonificacao(selecionada.instalacaoInfraId);
      } catch (causa) {
        setErro(mensagemDoErro(causa) ?? 'Não foi possível iniciar a personificação. Tente novamente.');
        setConfirmando(false);
        return;
      }
      // Melhor esforço, de propósito — ver `registrarPersonificacao`.
      await dataSource.registrarPersonificacao(selecionada.instalacaoInfraId);
    }
    definirPersonificacao({
      instalacaoId: selecionada.id,
      nome: selecionada.nome,
      instalacaoInfraId: selecionada.instalacaoInfraId,
    });
    aoFechar();
    // Recarrega de propósito: é a forma mais simples de garantir que toda
    // tela (cada uma com seu próprio `useResource`) refaça a busca já sob o
    // escopo da instalação personificada.
    window.location.reload();
  }

  return (
    <Dialog open={aberto} onOpenChange={(valor) => !valor && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Personificar Instalação</DialogTitle>
          <DialogDescription>
            Busque e selecione uma instalação para assumir acesso de administrador completo só
            sobre ela. Enquanto personificando, todas as suas instalações são substituídas por
            essa única — para voltar ao normal, use "Sair" na barra que aparece no topo.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <label htmlFor="busca-personificar" className="text-label text-on-surface">
            Instalação
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-icon"
              aria-hidden="true"
            />
            <Input
              id="busca-personificar"
              autoFocus
              autoComplete="off"
              value={busca}
              placeholder="Nome, código ou ID da instalação…"
              onChange={(evento) => {
                setBusca(evento.target.value);
                setSelecionada(null);
              }}
              className="pl-8"
            />
          </div>

          {carregando ? (
            <p className="text-cell text-on-surface-muted">Buscando…</p>
          ) : erro ? (
            <Callout tom="erro">{erro}</Callout>
          ) : busca.trim().length > 0 && busca.trim().length < CARACTERES_MINIMOS ? (
            <p className="text-cell text-on-surface-muted">
              Digite ao menos {CARACTERES_MINIMOS} caracteres.
            </p>
          ) : resultados.length > 0 ? (
            <ul
              role="listbox"
              aria-label="Instalações encontradas"
              className="pan-scroll-area max-h-56 overflow-y-auto rounded-input border border-border"
            >
              {resultados.map((item) => {
                const ativa = selecionada?.id === item.id;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={ativa}
                      onClick={() => setSelecionada(item)}
                      className={cn(
                        'flex w-full flex-col gap-0.5 border-b border-border-subtle px-3 py-2 text-left transition-colors duration-fast ease-standard last:border-b-0',
                        ativa ? 'bg-primary-wash' : 'hover:bg-bg-subtle',
                      )}
                    >
                      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-cell font-bold">
                        {item.nome}
                        <InstalacaoFlags ana={item.ana} interno={item.interno} />
                      </span>
                      <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">
                        {item.codigo ?? 'sem código'} · ID {item.id}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : busca.trim().length >= CARACTERES_MINIMOS ? (
            <p className="text-cell text-on-surface-muted">
              Nenhuma instalação encontrada para "{busca.trim()}".
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={!selecionada || confirmando} onClick={confirmar}>
            {confirmando ? 'Confirmando…' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
